import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { comingSoonDiscontinueDates } from "@tKgpco/Common/commonFunctions";
import SeeMoreInfoModal from "@tKgpco/Components/SeeMoreInfoModal";
import ViewProductButton from "@tKgpco/Overrides/Widgets/ProductList/ViewProductButton";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    isAuthenticated: state.context.session?.isAuthenticated && !state.context.session?.isGuest,
    properties: state.context.settings.properties,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    contentWrapper?: InjectableCss;
    productAvailabilityStyle?: ProductAvailabilityStyles;
    priceContainer?: InjectableCss;
    labels?: InjectableCss;
    seeLeadTimeLink?: LinkPresentationProps;
    seeLeadTimeLinkGridItem?: GridItemProps;
    tooltipText?: InjectableCss;
    badgeImgWrapper?: InjectableCss;
    itemNotFound?: InjectableCss;
    notFoundLabel?: TypographyPresentationProps;
    salesBlock?: InjectableCss;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 93%;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            `,
            typographyProps: {
                color: `${COLORS.lightBlack} !important`,
                weight: TYPOGRAPHY.weight.bold,
            },
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: {
            size: 14,
            color: COLORS.lightBlack,
            css: css`
                margin-right: 5px;
            `,
        },
        erpNumberValueText: { size: TYPOGRAPHY.size.smaller, weight: TYPOGRAPHY.weight.base, color: COLORS.lightBlack },
        customerNameLabelText: { size: 14, color: COLORS.lightBlack },
        customerNameValueText: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.base,
            color: COLORS.lightBlack,
        },
        manufacturerItemLabelText: { size: 14, color: COLORS.lightBlack },
        manufacturerItemValueText: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.base,
            color: COLORS.lightBlack,
        },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            color: ${COLORS.link.base};
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 5px;
                flex-direction: row;
                align-items: center;
                background-color: #e1e7ed;
                color: ${COLORS.lightBlack};
                border-radius: 8px;
                label {
                    text-align: center;
                    padding: 8px;
                    width: 40%;
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 13px !important;
                    }
                }
                & > div {
                    margin-top: 0 !important;
                    width: 100%;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
                select[data-test-selector="product_unitOfMeasureSelect"] {
                    text-align: Center !important;
                    padding-left: 10px !important;
                    padding-right: 2px !important;
                    font-size: 14px !important;
                    border-radius: 0px 8px 8px 0px !important;
                    background-color: #f1f6fb !important;
                    border: 0 none !important;

                    & :focus {
                        padding-right: 2px !important;
                        padding-left: 10px !important;
                    }
                    + span {
                        background: transparent;
                    }
                }
            `,
        },
        backgroundColor: "#e1e7ed",
    },
    addToCartContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            background: #e1e7ed;
            border-radius: 0 10px;
            padding-left: 12px;
        `,
    },
    qtyGridItem: {
        width: 4,
        css: css`
            padding-bottom: 0;
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 5px;
                flex-direction: row;
                align-items: center;
                background-color: #e1e7ed;
                color: ${COLORS.lightBlack};
                border-radius: 8px;
                label {
                    text-align: center;
                    padding: 8px;
                    width: 40%;
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 13px !important;
                    }
                }
                & > div {
                    margin-top: 0 !important;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
                input[data-test-selector="product_qtyOrdered"] {
                    text-align: center !important;
                    padding-left: 0 !important;
                    padding-right: 2px !important;
                    font-size: 14px !important;
                    border-radius: 0px 8px 8px 0px !important;
                    border: 0 none !important;
                    & :focus {
                        padding-left: 0 !important;
                        padding-right: 2px !important;
                    }
                }
            `,
        },
        backgroundColor: "#e1e7ed",
    },
    addToCartButtonGridItem: {
        width: 8,
        css: css`
            padding-bottom: 0;
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
            border-radius: 0px 0 8px 8px;
            background: transparent linear-gradient(180deg, #44a8f7 0%, #1e75ba 100%) 0% 0% no-repeat;
            border: none;
            text-transform: uppercase;
            font-size: ${TYPOGRAPHY.size.smaller};
            background-color: #1e75ba;
            color: ${COLORS.white};
            &:focus {
                outline: none;
            }
            &:hover {
                background: ${COLORS.link.base};
            }
        `,
        hoverStyle: {
            backgroundColor: "transparent linear-gradient(180deg, #44a8f7 0%, #1e75ba 100%) 0% 0% no-repeat;",
        },
        typographyProps: {
            size: 14,
        },
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: {
                size: 14,
                color: `${COLORS.black} !important`,
                css: css`
                    &:hover {
                        color: ${COLORS.primary.base} !important;
                        text-decoration: underline;
                    }
                `,
            },
            css: css`
                position: absolute;
                top: 8px;
                right: 1.5rem;
            `,
        },
    },
    productBrandStyles: {
        nameLink: {
            typographyProps: {
                size: 14,
            },
        },
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 12px;
            flex-grow: 1;
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                justify-content: center;
                /*height: 170px;*/
                max-width: 100%;
                width: 100%;
                img {
                    height: 149px;
                    width: auto;
                    margin: auto;
                    margin-top: 28px;
                    max-width: 100%;
                    object-fit: cover;
                }
            `,
        },
    },
    productAvailabilityStyle: {
        messageGridItem: {
            css: css`
                justify-content: flex-start;
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.bold};
            `,
        },
        availabilityByWarehouseLinkGridItem: {
            css: css`
                justify-content: flex-end;
            `,
        },
    },
    priceContainer: {
        css: css`
            display: flex;
            margin: 10px 0 5px;
            color: ${COLORS.link.base};
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    labels: {
        css: css`
            font-size: 14px;
            margin-bottom: 5px;
        `,
    },
    seeLeadTimeLink: {
        css: css`
            & :hover .tooltipText {
                visibility: visible;
            }
        `,
    },
    seeLeadTimeLinkGridItem: {
        width: 5,
        css: css`
            margin: 10px 0px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-end;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            > span {
                background: #e1e7ed;
                padding: 3px 4px;
                border-radius: 4px;
                > span {
                    color: #353432 !important;
                    font-size: ${TYPOGRAPHY.size.vTiny};
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 10px !important;
                    }
                }
            }
        `,
    },
    tooltipText: {
        css: css`
            visibility: hidden;
            width: auto;
            background-color: black;
            color: #fff;
            text-align: center;
            border-radius: 6px;
            padding: 5px 6px;
            opacity: 0.8;
            position: absolute;
            z-index: 1;
            margin-top: 10px;
        `,
    },
    badgeImgWrapper: {
        css: css`
            position: absolute;
            left: 50%;
            top: 50%;
            width: 25%;
            height: 28%;
            transform: translate(-50%, -50%);
            pointer-events: none;
        `,
    },
    itemNotFound: {
        css: css`
            font-size: 14px;
            span {
                padding-right: 5px;
            }
        `,
    },
    notFoundLabel: {
        size: 14,
        weight: 700,
        color: `${COLORS.black}`,
    },
    salesBlock: {
        css: css`
            border: 2px solid;
            border-radius: 4px;
            padding: 5px;
            color: #f0b003;
            margin: 10px 0 3px;
            font-weight: bold;
            font-size: 14px;
            width: max-content;
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    isAuthenticated,
    properties,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    const { isComingSoon, isDiscontinued } = comingSoonDiscontinueDates(productContext.product);
    const { comingSoonImagePath, discontinuedImagePath } = properties;
    let leadtime = "";
    if (productInfo?.pricing?.additionalResults && Object.keys(productInfo?.pricing?.additionalResults).length > 0) {
        leadtime = productInfo?.pricing?.additionalResults?.leadTime;
    }
    let showPriceMessage = true;
    if (productInfo?.pricing?.additionalResults?.showContactUsMessage?.toLowerCase() === "true") {
        showPriceMessage = false;
    }

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper {...styles.contentWrapper}>
                    {showImage && !showOverlayBadge && (
                        <StyledWrapper {...styles.badgeAreaWrapper}>
                            <ProductImage
                                product={productContext}
                                onClick={() => trackProductRecommendation(true)}
                                extendedStyles={styles.productImageStyles}
                            />
                            {isAuthenticated && isComingSoon && (
                                <StyledWrapper className="coming-soon" {...styles.badgeImgWrapper}>
                                    <LazyImage src={comingSoonImagePath} />
                                </StyledWrapper>
                            )}
                            {isAuthenticated && isDiscontinued && (
                                <StyledWrapper className="discontinued" {...styles.badgeImgWrapper}>
                                    <LazyImage src={discontinuedImagePath} />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    )}
                    {showImage && showOverlayBadge && overlayBadge && (
                        <StyledWrapper
                            {...styles.badgeAreaWrapper}
                            data-test-selector="productCarouselBadge"
                            ref={badgeAreaWrapperRef}
                        >
                            <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                            <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                <Badge
                                    type={overlayBadge.badgeType}
                                    value={
                                        overlayBadge.badgeType === "Image"
                                            ? overlayBadge.largeImageBadgePath
                                            : overlayBadge.displayText
                                    }
                                    badgeTextColor={overlayBadge.textColorHexCode}
                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                    imageAltText={
                                        overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                    }
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {showBrand && product.brand && (
                        <StyledWrapper {...styles.productBrandWrapper}>
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        </StyledWrapper>
                    )}
                    {showTitle && (
                        <ProductDescription
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productDescriptionStyles}
                        />
                    )}
                    {showPartNumbers && (
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            showCustomerName={false}
                            showManufacturerItem={false}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    )}
                    {showTextBadges && textBadges && (
                        <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                    )}
                    {showImageBadges && imageBadges && (
                        <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                    )}
                    {showPrice && showPriceMessage && (
                        <StyledWrapper {...styles.productPriceWrapper}>
                            <ProductContextAvailability
                                isProductDetailsPage={false}
                                extendedStyles={styles.productAvailabilityStyle}
                            />
                            <StyledWrapper>
                                <Typography {...styles.notFoundLabel}>{translate("Your Price:")}</Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.priceContainer}>
                                <ProductPrice showLabel={false} product={productContext} />
                            </StyledWrapper>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                                {translate("U/M")}
                            </VisuallyHidden>
                            {product.unitOfMeasures && (
                                <ProductUnitOfMeasureSelect
                                    labelOverride="U/M"
                                    extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                    id={`${carouselId}-uom`}
                                />
                            )}
                            <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
                        </StyledWrapper>
                    )}
                    {!showPriceMessage ? (
                        <>
                            <StyledWrapper>
                                <Typography {...styles.notFoundLabel}>{translate("List Price:")}</Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.priceContainer}>
                                <ProductPrice showLabel={false} product={productContext} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.itemNotFound}>
                                <Link href="/contact-kgpco">{translate("Contact Us")}</Link>
                                <Typography>{translate("for pricing & availability for this item.")}</Typography>
                            </StyledWrapper>{" "}
                        </>
                    ) : null}
                    {showAddToList && showPriceMessage && (
                        <ProductAddToListLink
                            data-test-selector="addToListLink"
                            extendedStyles={styles.productAddToListLinkStyles}
                        />
                    )}

                    {showAddToCart && showPriceMessage && (
                        <StyledWrapper>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                {translate("QTY")}
                            </VisuallyHidden>
                            <ProductQuantityOrdered
                                labelOverride="QTY"
                                extendedStyles={styles.productQuantityOrderedStyles}
                                id={`${carouselId}-qty`}
                            />
                        </StyledWrapper>
                    )}
                    {isAuthenticated &&
                        leadtime &&
                        leadtime !== "" &&
                        productInfo?.pricing?.additionalResults?.showContactUsMessage !== "true" && (
                            <GridItem {...styles.seeLeadTimeLinkGridItem}>
                                <Link {...styles.seeLeadTimeLink} data-test-selector="seeLeadTimeLink">
                                    {translate("See Lead Time")}
                                    <StyledWrapper
                                        className="tooltipText"
                                        {...styles.tooltipText}
                                    >{`${leadtime} days`}</StyledWrapper>
                                </Link>
                            </GridItem>
                        )}
                    <GridItem width={12}>{isAuthenticated && <SeeMoreInfoModal product={product} />}</GridItem>
                </StyledWrapper>
                {/* {showAddToCart && (
                    <GridContainer {...styles.addToCartContainer}>
                        <GridItem {...styles.qtyGridItem}>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                {translate("QTY")}
                            </VisuallyHidden>
                            <ProductQuantityOrdered
                                labelOverride="QTY"
                                extendedStyles={styles.productQuantityOrderedStyles}
                                id={`${carouselId}-qty`}
                            />
                        </GridItem>
                        <GridItem {...styles.addToCartButtonGridItem}>
                            <ProductAddToCartButton
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </GridItem>
                    </GridContainer>
                )} */}

                {product.properties?.salesBlocked?.toLowerCase() === "true" ? (
                    <StyledWrapper {...styles.salesBlock}>{siteMessage("sales_block_product_msg")}</StyledWrapper>
                ) : null}
                {productContext.productInfo && productContext.productInfo.pricing && showAddToCart && (
                    <ProductAddToCartButton
                        onAddToCartClick={() => trackProductRecommendation(false)}
                        data-test-selector="addToCartBtn"
                        extendedStyles={styles.productAddToCartButtonStyles}
                    />
                )}
                <ViewProductButton position="left" productInfo={productContext.productInfo} />
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
